"use client";

import { useEffect, useRef, useState } from "react";
import audioManager from "../../utils/AudioManajer";

export default function Slider({ onValueChange, value: propValue }) {
  const [value, setValue] = useState(propValue);
  const audioCtxRef = useRef(null);
  const bufferRef = useRef(null);
  const gainNodeRef = useRef(null);
  const isUnlocked = useRef(false);
  const sliderRef = useRef(null);
  const spanRef = useRef(null);

  useEffect(() => {
    setValue(propValue);
  }, [propValue]);

  const unlockOnGesture = async () => {
    try {
      // Create AudioContext
      if (!audioCtxRef.current) {
        const AudioContextClass =
          window.AudioContext || window.webkitAudioContext;
        audioCtxRef.current = new AudioContextClass();

        // Register with audio manager
        audioManager.registerAudioContext(audioCtxRef.current);

        // Create and register gain node
        gainNodeRef.current = audioCtxRef.current.createGain();
        gainNodeRef.current.connect(audioCtxRef.current.destination);
        audioManager.registerGainNode(gainNodeRef.current);
      }

      // CRITICAL: Resume context for Android browsers
      if (audioCtxRef.current.state === "suspended") {
        await audioCtxRef.current.resume();
        console.log("AudioContext resumed");
      }

      // Play silent buffer
      const silentBuffer = audioCtxRef.current.createBuffer(1, 1, 22050);
      const silentSource = audioCtxRef.current.createBufferSource();
      silentSource.buffer = silentBuffer;
      silentSource.connect(gainNodeRef.current);
      silentSource.start();

      // Load audio
      if (!isUnlocked.current) {
        const res = await fetch("/tik.mp3");
        const arrayBuffer = await res.arrayBuffer();
        const buffer = await new Promise((resolve, reject) => {
          audioCtxRef.current.decodeAudioData(arrayBuffer, resolve, reject);
        });
        bufferRef.current = buffer;
        isUnlocked.current = true;
        console.log("Audio unlocked");
      }

      // Remove listeners
      sliderRef.current?.removeEventListener("touchstart", unlockOnGesture);
      sliderRef.current?.removeEventListener("mousedown", unlockOnGesture);
    } catch (err) {
      console.error("Audio unlock failed:", err);
    }
  };

  const playSound = async () => {
    if (!audioCtxRef.current || !bufferRef.current) {
      console.log("Audio not ready");
      return;
    }

    try {
      // Resume context if needed (critical for Android)
      if (audioCtxRef.current.state === "suspended") {
        await audioCtxRef.current.resume();
      }

      if (!isUnlocked.current) {
        console.log("Audio not unlocked yet");
        return;
      }

      const source = audioCtxRef.current.createBufferSource();
      source.buffer = bufferRef.current;
      source.connect(gainNodeRef.current);
      source.start();
      console.log("Sound played");
    } catch (err) {
      console.error("Play sound error:", err);
    }
  };

  const handleSliderInput = (e) => {
    const val = parseInt(e.target.value);
    setValue(val);
    playSound();
    onValueChange(val);
  };

  const updateLabelPosition = () => {
    const slider = sliderRef.current;
    const label = spanRef.current;
    if (!slider || !label) return;

    const sliderWidth = slider.offsetWidth;
    const min = parseInt(slider.min);
    const max = parseInt(slider.max);
    const percent = (value - min) / (max - min);
    const thumbSize = 20;
    const offset = percent * (sliderWidth - thumbSize) + thumbSize / 2;

    label.style.left = `${offset}px`;
  };

  useEffect(() => {
    updateLabelPosition();
  }, [value]);

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    slider.addEventListener("touchstart", unlockOnGesture, { passive: true });
    slider.addEventListener("mousedown", unlockOnGesture, { passive: true });

    return () => {
      slider.removeEventListener("touchstart", unlockOnGesture);
      slider.removeEventListener("mousedown", unlockOnGesture);

      if (gainNodeRef.current) {
        audioManager.unregisterGainNode(gainNodeRef.current);
      }
    };
  }, []);

  return (
    <div className="mainsliders">
      <div className="numbersdistribute">
        <p className="numberpara">1%</p>
        <p className="numberpara">25%</p>
        <p className="numberpara">50%</p>
        <p className="numberpara">75%</p>
        <p className="numberpara">94%</p>
      </div>
      <div style={{ position: "relative", width: "100%", padding: "11px 0 0" }}>
        <input
          ref={sliderRef}
          type="range"
          min="1"
          max="94"
          value={value}
          onInput={handleSliderInput}
          style={{
            width: "100%",
            appearance: "none",
            height: "15px",
            borderRadius: "12px",
            background: `linear-gradient(to right, #F24CA2 0%, #F24CA2 ${
              ((value - 1) / (94 - 1)) * 100
            }%, var(--bet-bg) ${
              ((value - 1) / (94 - 1)) * 100
            }%, var(--bet-bg) 100%)`,
            outline: "none",
            position: "relative",
            zIndex: 2,
          }}
        />
        <span
          ref={spanRef}
          style={{
            position: "absolute",
            transform: "translateX(-50%)",
            transition: "left 0.05s",
            whiteSpace: "nowrap",
          }}
          className="mainspan"
        >
          {value}%
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="10"
            height="3"
            viewBox="0 0 10 3"
            fill="none"
            className="innerarrow"
            style={{ display: "block", margin: "2px auto 0" }}
          >
            <path
              d="M6.54349 2.07391C5.59344 2.64394 4.40656 2.64394 3.45651 2.07391L0 0L10 0L6.54349 2.07391Z"
              fill="#F24CA2"
            />
          </svg>
        </span>
      </div>
    </div>
  );
}
